import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionSummary,
  Button,
  Container,
  Grid,
  IconButton,
  Pagination,
  Skeleton,
  styled,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import { EditorContent } from '@tiptap/react';
import React from 'react';
import { Link as ReactLink } from 'react-router-dom';

export const StyledLink = styled(ReactLink)({
  textDecoration: 'none',
  color: 'inherit',
  '&:active, &:visited, &:hover, &:focus': {
    color: 'inherit',
  },
});

export const StyledChatContainer = styled(Container)({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  marginLeft: 'auto',
  marginRight: 'auto',
  justifyContent: 'space-between',
  '& > *:not(:last-child)': {
    textAlign: 'center',
  },
});

export const ChatContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  backgroundColor: 'white',
});

export const BouncingText = styled('span')(
  ({ idx, shouldBounce }: { idx: number; shouldBounce: boolean }) => ({
    display: 'inline-block',
    animation: shouldBounce ? `bounceLetter 0.8s ${idx * 0.08}s infinite` : 'none',
    '@keyframes bounceLetter': {
      '0%, 50%, 100%': { transform: 'translateY(0)', opacity: 1 },
      '30%': { transform: 'translateY(-8px)', opacity: 0.7 },
    },
  })
);

export const StyledDrawerButton = styled(Button)<{ isActive?: boolean }>(({ isActive }) => ({
  width: '100%',
  minWidth: '0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '5px',
  marginTop: '5px',
  backgroundColor: isActive ? '#E8EEF84C' : 'transparent',
  boxShadow: 'none',
  border: '1px solid transparent',
  ...(isActive
    ? {
        borderLeft: '5px solid #0047FF',
        paddingLeft: '11px',
      }
    : { paddingLeft: '13px' }),
  '&:hover': {
    backgroundColor: '#E8EEF84C',
    borderColor: '#0047FF',
    borderLeft: isActive ? '5px solid #0047FF' : '1px solid #0047FF',
    color: '#070154',
  },
}));

export const ProfileAndNotesDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginTop: '20px',
  paddingRight: '200px',
  paddingLeft: '200px',
});

export const StyledGridContainer = styled(Grid)({
  display: 'flex',
  flexGrow: 1,
});

export const TiptapStyled = styled(EditorContent)`
  .ProseMirror {
    border: none;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.5;
    max-height: calc(1.5em * 5);
    min-height: calc(1.5em * 2);
    outline: none;

    overflow-y: auto;
    padding-bottom: 3px;
    padding-left: 5px;
    padding-right: 75px;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

export const TiptapWrapper = styled('fieldset')({
  position: 'relative',
  display: 'block',
  alignItems: 'center',
  width: '100%',
  border: '2px solid #ccc',
  borderRadius: '4px',
  padding: '0',

  backgroundColor: 'white',
  '&:focus-within': {
    borderColor: '#1C86E3',
    outline: 'none',
    '& legend': {
      color: '#1C86E3',
    },
  },
});

export const PromptEditTiptapStyled = styled(EditorContent)`
  .ProseMirror {
    border: none;
    bottom: 5px;
    font-size: 16px;
    min-height: 120px;
    outline: none;
    padding-bottom: 70px;
    padding-left: 10px;
    padding-right: 50px;
  }
`;

export const PromptEditTiptapWrapper = styled('fieldset')({
  position: 'relative',
  display: 'block',
  alignItems: 'center',
  width: '100%',
  border: '1px solid lightgray',
  borderRadius: '4px',
  padding: '0',
  '&:hover': {
    borderColor: '#F900D3',
    border: '2px solid #F900D3',
    '& legend': {
      color: '#F900D3',
    },
  },
  '&:focus-within': {
    borderColor: '#0047FF',
    border: '2px solid #0047FF',
    outline: 'none',
    '& legend': {
      color: '#0047FF',
    },
  },
});

export const PromptEditTiptapLabel = styled('legend')({
  padding: '0 5px',
  fontSize: '13px',
  color: '#666',
  fontWeight: '400',
  letterSpacing: '0.03333em',
});

export const PromptEditHelperText = styled('p')({
  fontSize: '0.75rem',
  color: 'rgba(0, 0, 0, 0.6)',
  fontWeight: '400',
  letterSpacing: '0.03333em',
  marginLeft: '14px',
  lineHeight: '1.66',
  marginTop: '0px',
  marginBottom: '0',
});

export const IconsContainer = styled('div')({
  display: 'flex',
  gap: '20px',
});

export const IconContainer = styled('span')({
  color: 'gray',
  transition: 'all 0.2s ease-in-out',
  display: 'flex',
  height: '20px',
  cursor: 'pointer',
  span: {
    color: 'black',
    marginLeft: '5px',
  },
});

export const ChatRow = styled('div')({
  display: 'flex',
  gap: '5px',
  marginBottom: '16px',
  alignItems: 'flex-start',
  paddingTop: '10px',
  paddingLeft: '24px',
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: '900px',
  width: '100%',
});

export const StyledTabs = styled(Tabs)({
  borderBottom: '1px solid #C4C4C4',
  marginBottom: '16px',
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-flexContainer': {
    flexWrap: 'wrap',
  },
});

export const StyledTab = styled(Tab)({
  textTransform: 'none',

  '&.Mui-selected': {
    color: '#070154',
    borderBottom: '2px solid #070154',
  },
  '&:hover': {
    color: '#F900D3',
  },
});

export const StyledPagination = styled(Pagination)({
  'button.Mui-selected': {
    backgroundColor: 'rgb(232, 238, 248)',
    color: '#070154',
  },
  'button.MuiPaginationItem-previousNext': {
    color: '#070154',
    opacity: 1,
  },
  'button.MuiButtonBase-root': {
    color: '#070154',
  },
});

export const DynamicText = styled('span')<{
  fontWeight?: string | number;
  color?: string;
}>(({ fontWeight = '700', color = 'inherit' }) => ({
  fontWeight: fontWeight,
  color: color,
}));

export const StyledLoadingButton = styled(LoadingButton)({
  padding: '8px 22px',
  width: '224px',
  height: '36px',
  boxShadow:
    '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
});

export const StyledAccordion = styled(Accordion)({
  borderBottom: `1px solid #C4C4C4`,
  '&:last-child': { borderBottom: 0 },
  '&.Mui-expanded': { margin: 0 },
});

export const StyledAccordionSummary = styled(AccordionSummary)({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: '24px',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    marginLeft: '24px',
  },
});

export const IconLabel = styled(Typography)({
  alignItems: 'center',
  display: 'flex',
  gap: '5px',
  textAlign: 'center',
});

export const StyledTableRow = styled(TableRow)({
  '&:nth-of-type(even)': {
    backgroundColor: '#E8EEF8',
  },
});

export const StyledTableCell = styled(TableCell)({
  padding: '12px 16px',
});

export const StyledTableHeaderCell = styled(StyledTableCell)({
  fontWeight: '700',
  borderBottom: '2px solid #C4C4C4',
});

export const StyledSkeleton = styled(Skeleton)({
  animation: 'whitePulse 3s ease-in-out infinite',
  backgroundColor: 'white',
  '@keyframes whitePulse': {
    '0%': {
      backgroundColor: 'white',
    },
    '50%': {
      backgroundColor: '#e0e0e0', // default MUI gray
    },
    '100%': {
      backgroundColor: 'white',
    },
  },
});

export const CustomTooltip = styled((props: TooltipProps & { className?: string }) => (
  <Tooltip {...props} classes={{ popper: props.className }} arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'black',
    maxWidth: 400,
    maxHeight: 400,
    padding: 20,
    overflowY: 'auto',
    border: '1px solid #ccc',
    boxShadow: theme.shadows[3],
    fontSize: '0.875rem',
    whiteSpace: 'pre-line',
  },
}));

export const StyledIconButton = styled(IconButton)({
  width: '40px',
  height: '40px',
});

export const LinkDiv = styled('div')({
  color: '#070154',
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '0',
  marginBottom: '0',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
});

export const LikeContainer = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'liked',
})<{ liked?: boolean }>(({ liked }) => ({
  color: liked ? '#014361' : '#767676',
  border: liked ? '1px solid #014361' : '1px solid #767676',
  height: '18px',
  width: '18px',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: '2px',
  marginLeft: '5px',
  backgroundColor: liked ? '#cce6ff' : '#fff',

  '& svg': {
    width: '14px',
    height: '14px',
  },

  ':hover': {
    cursor: 'pointer',
    backgroundColor: liked ? '#95c4da' : '#d9d9d9',
  },
}));
